import { type FilterState, filterStateToSearch, skillFilters } from '@parcel/filters';
import {
  SKILL_PAGE_MAX_LIMIT,
  SKILL_QUERY_MAX_LENGTH,
  type SkillAudience,
  SkillAudienceSchema,
  type SkillBundleInput,
  type SkillFileInput,
  type SkillLifecycle,
  type SkillLocator,
  SkillLocatorSchema,
  type SkillWriteReceipt,
  SkillWriteReceiptSchema,
} from '@parcel/types';
import { ApiError, apiFetch } from './api.js';

/**
 * Typed browser client over the PR 15 `/workspace/skills/*` routes, plus the
 * browser projections the Skills page keys and edits with. The member's bearer
 * token arrives through the shared `apiFetch`; the workspace is always explicit.
 */

// ---------------------------------------------------------------------------
// Browser projections
// ---------------------------------------------------------------------------

/** The page's three data sources. `section` selects a source, never a predicate. */
export type SkillsSection = 'personal' | 'workspace' | 'explore';

export type SkillEditorMode = 'create' | 'edit' | 'restore';

export interface SkillsPageState {
  section: SkillsSection;
  filters: FilterState;
}

export interface SkillEditorDraft {
  skillId: string | null;
  audience: SkillAudience;
  draftVersion: number | null;
  baseRevisionId: string | null;
  files: readonly SkillFileInput[];
}

// ---------------------------------------------------------------------------
// Wire types
// ---------------------------------------------------------------------------

export type SkillCatalogState = 'ready' | 'not_populated' | 'unavailable' | 'disabled';
export type SkillCatalogSource = 'custom' | 'official';
export type SkillDetailView = 'published' | 'draft';

export interface SkillSummary {
  locator: SkillLocator;
  name: string;
  description: string;
  audience: SkillAudience;
  source: SkillCatalogSource;
  lifecycle: SkillLifecycle;
  slug: string;
  createdBy: string | null;
  maintainerId: string | null;
  installed: boolean;
  revision: string;
  sha256: string;
  updatedAt: string;
  /** A published custom skill whose saved draft differs from what is live. */
  hasUnpublishedChanges: boolean;
}

export interface SkillFileEntry {
  path: string;
  mediaType: string;
  bytes: number;
  sha256: string;
}

export interface SkillDetail extends SkillSummary {
  skillMd: string;
  instructions: string;
  license: string | null;
  compatibility: string | null;
  metadata: Readonly<Record<string, unknown>> | null;
  files: readonly SkillFileEntry[];
  revisionId: string;
  bundleSha256: string;
  view: SkillDetailView;
  /** The expected versions a trusted write sends back; null when this view has none. */
  draftVersion: number | null;
  version: number | null;
  installationId: string | null;
  installationVersion: number | null;
}

export interface SkillFileDetail {
  path: string;
  mediaType: string;
  content: string;
  bytes: number;
  sha256: string;
  revisionId: string;
}

export interface SkillCatalogResult {
  state: SkillCatalogState;
  items: readonly SkillSummary[];
}

export interface SkillRevisionSummary {
  revisionId: string;
  revision: number;
  bundleSha256: string;
  publishedAt: string;
  publishedBy: string | null;
}

export interface SkillRevisionPage {
  items: readonly SkillRevisionSummary[];
  nextCursor: string | null;
}

export type { SkillWriteReceipt };

export type SkillOverrideInput =
  | { kind: 'custom'; skillId: string }
  | { kind: 'official'; officialSkillId: string };

export interface FindSkillInput {
  query: string;
  limit?: number;
  scope?: 'active' | 'explore';
}

export type SkillListSort = 'name' | 'updated_at';
export type SkillListLifecycle = Exclude<SkillLifecycle, 'deleted'>;

/** Mirrors the `skill` facets in @parcel/filters, plus sort and paging. */
export interface ListSkillsInput {
  q?: string;
  source?: SkillCatalogSource[];
  lifecycle?: SkillListLifecycle[];
  audience?: SkillAudience[];
  installed?: 'installed' | 'not_installed';
  updated_after?: string;
  updated_before?: string;
  sort?: SkillListSort;
  direction?: 'asc' | 'desc';
  limit?: number;
}

export interface GetSkillInput {
  locator: SkillLocator;
}

export interface GetSkillFileInput {
  locator: SkillLocator;
  path: string;
}

export interface ListSkillRevisionsInput {
  skillId: string;
  limit?: number;
}

/** Every write may carry its own key so a retry is the same write, not a second one. */
interface IdempotentInput {
  idempotencyKey?: string;
}

export interface CreateSkillInput extends IdempotentInput {
  audience: SkillAudience;
  bundle: SkillBundleInput;
  override?: SkillOverrideInput;
  authoringConversationId?: string;
}

export interface UpdateSkillInput extends IdempotentInput {
  skillId: string;
  expectedDraftVersion: number;
  bundle?: SkillBundleInput;
  restoreRevisionId?: string;
}

export interface PublishSkillInput extends IdempotentInput {
  skillId: string;
  expectedDraftVersion: number;
}

export interface UnpublishSkillInput extends IdempotentInput {
  skillId: string;
  expectedVersion: number;
}

export interface DeleteSkillInput extends IdempotentInput {
  skillId: string;
  expectedVersion: number;
}

export interface TransferSkillInput extends IdempotentInput {
  skillId: string;
  expectedVersion: number;
  newMaintainerId: string;
}

export interface MoveSkillInput extends IdempotentInput {
  skillId: string;
  expectedVersion: number;
  audience: SkillAudience;
}

export interface ForkSkillInput extends IdempotentInput {
  source: SkillLocator;
  audience: SkillAudience;
  override?: SkillOverrideInput;
  name?: string;
}

export interface InstallSkillInput extends IdempotentInput {
  officialSkillId: string;
  releaseId?: string;
  audience: SkillAudience;
}

export interface UninstallSkillInput extends IdempotentInput {
  installationId: string;
  expectedVersion: number;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const skillQueryKeys = {
  all: (workspaceId: string) => ['skills', workspaceId] as const,
  // Keyed on the canonical FilterState, so the key and the request cannot drift.
  catalog: (workspaceId: string, section: SkillsSection, filters: FilterState) =>
    ['skills', workspaceId, 'catalog', section, filters] as const,
  // PR 15 Ruling 61: an official get is actor-relative, so a detail entry is scoped per member.
  detail: (workspaceId: string, memberId: string, locator: SkillLocator) =>
    ['skills', workspaceId, 'detail', memberId, locator] as const,
  revisions: (workspaceId: string, skillId: string) =>
    ['skills', workspaceId, 'revisions', skillId] as const,
};

// ---------------------------------------------------------------------------
// Response parsing
// ---------------------------------------------------------------------------

/** A response (or an outgoing key) the browser refuses to guess at. */
export class SkillResponseError extends Error {
  readonly code = 'skill_response_unreadable';

  constructor(what: string) {
    super(`Unreadable skill response: ${what}.`);
    this.name = 'SkillResponseError';
  }
}

function record(raw: unknown, what: string): Record<string, unknown> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new SkillResponseError(what);
  return raw as Record<string, unknown>;
}

function only(row: Record<string, unknown>, allowed: readonly string[], what: string): void {
  for (const key of Object.keys(row)) {
    if (!allowed.includes(key)) throw new SkillResponseError(`${what} carries ${key}`);
  }
}

function str(row: Record<string, unknown>, key: string, what: string): string {
  const value = row[key];
  if (typeof value !== 'string') throw new SkillResponseError(`${what}.${key}`);
  return value;
}

function nullableStr(row: Record<string, unknown>, key: string, what: string): string | null {
  const value = row[key];
  if (value === null) return null;
  if (typeof value !== 'string') throw new SkillResponseError(`${what}.${key}`);
  return value;
}

function nullableInt(row: Record<string, unknown>, key: string, what: string): number | null {
  if (row[key] === null) return null;
  return int(row, key, what);
}

function int(row: Record<string, unknown>, key: string, what: string): number {
  const value = row[key];
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    throw new SkillResponseError(`${what}.${key}`);
  }
  return value;
}

function bool(row: Record<string, unknown>, key: string, what: string): boolean {
  const value = row[key];
  if (typeof value !== 'boolean') throw new SkillResponseError(`${what}.${key}`);
  return value;
}

function oneOf<T extends string>(
  row: Record<string, unknown>,
  key: string,
  values: readonly T[],
  what: string,
): T {
  const value = row[key];
  if (typeof value !== 'string' || !values.includes(value as T)) {
    throw new SkillResponseError(`${what}.${key}`);
  }
  return value as T;
}

function locatorOf(row: Record<string, unknown>, what: string): SkillLocator {
  const parsed = SkillLocatorSchema.safeParse(row.locator);
  if (!parsed.success) throw new SkillResponseError(`${what}.locator`);
  return parsed.data;
}

const CATALOG_STATES: readonly SkillCatalogState[] = [
  'ready',
  'not_populated',
  'unavailable',
  'disabled',
];
const CATALOG_SOURCES: readonly SkillCatalogSource[] = ['custom', 'official'];
const DETAIL_VIEWS: readonly SkillDetailView[] = ['published', 'draft'];

const SUMMARY_KEYS = [
  'locator',
  'name',
  'description',
  'audience',
  'source',
  'lifecycle',
  'slug',
  'createdBy',
  'maintainerId',
  'installed',
  'revision',
  'sha256',
  'updatedAt',
  'hasUnpublishedChanges',
] as const;

const DETAIL_KEYS = [
  ...SUMMARY_KEYS,
  'skillMd',
  'instructions',
  'license',
  'compatibility',
  'metadata',
  'files',
  'revisionId',
  'bundleSha256',
  'view',
  'draftVersion',
  'version',
  'installationId',
  'installationVersion',
] as const;

function readSummary(raw: unknown): SkillSummary {
  const row = record(raw, 'summary');
  only(row, SUMMARY_KEYS, 'summary');
  return {
    locator: locatorOf(row, 'summary'),
    name: str(row, 'name', 'summary'),
    description: str(row, 'description', 'summary'),
    audience: oneOf(row, 'audience', SkillAudienceSchema.options, 'summary'),
    source: oneOf(row, 'source', CATALOG_SOURCES, 'summary'),
    lifecycle: oneOf(
      row,
      'lifecycle',
      ['draft', 'published', 'unpublished', 'deleted'] as const,
      'summary',
    ),
    slug: str(row, 'slug', 'summary'),
    createdBy: nullableStr(row, 'createdBy', 'summary'),
    maintainerId: nullableStr(row, 'maintainerId', 'summary'),
    installed: bool(row, 'installed', 'summary'),
    revision: str(row, 'revision', 'summary'),
    sha256: str(row, 'sha256', 'summary'),
    updatedAt: str(row, 'updatedAt', 'summary'),
    hasUnpublishedChanges: bool(row, 'hasUnpublishedChanges', 'summary'),
  };
}

export function parseSkillCatalogResult(raw: unknown): SkillCatalogResult {
  const row = record(raw, 'catalog result');
  only(row, ['state', 'items'], 'catalog result');
  if (!Array.isArray(row.items)) throw new SkillResponseError('catalog result.items');
  return {
    state: oneOf(row, 'state', CATALOG_STATES, 'catalog result'),
    items: row.items.map(readSummary),
  };
}

function readFileEntry(raw: unknown): SkillFileEntry {
  const row = record(raw, 'file entry');
  only(row, ['path', 'mediaType', 'bytes', 'sha256'], 'file entry');
  return {
    path: str(row, 'path', 'file entry'),
    mediaType: str(row, 'mediaType', 'file entry'),
    bytes: int(row, 'bytes', 'file entry'),
    sha256: str(row, 'sha256', 'file entry'),
  };
}

export function parseSkillDetail(raw: unknown): SkillDetail {
  const row = record(raw, 'skill detail');
  only(row, DETAIL_KEYS, 'skill detail');
  if (!Array.isArray(row.files)) throw new SkillResponseError('skill detail.files');
  const metadata = row.metadata === null ? null : record(row.metadata, 'skill detail.metadata');
  return {
    ...readSummary(Object.fromEntries(SUMMARY_KEYS.map((key) => [key, row[key]]))),
    skillMd: str(row, 'skillMd', 'skill detail'),
    instructions: str(row, 'instructions', 'skill detail'),
    license: nullableStr(row, 'license', 'skill detail'),
    compatibility: nullableStr(row, 'compatibility', 'skill detail'),
    metadata,
    files: row.files.map(readFileEntry),
    revisionId: str(row, 'revisionId', 'skill detail'),
    bundleSha256: str(row, 'bundleSha256', 'skill detail'),
    view: oneOf(row, 'view', DETAIL_VIEWS, 'skill detail'),
    draftVersion: nullableInt(row, 'draftVersion', 'skill detail'),
    version: nullableInt(row, 'version', 'skill detail'),
    installationId: nullableStr(row, 'installationId', 'skill detail'),
    installationVersion: nullableInt(row, 'installationVersion', 'skill detail'),
  };
}

export function parseSkillFileDetail(raw: unknown): SkillFileDetail {
  const row = record(raw, 'skill file');
  only(row, ['path', 'mediaType', 'content', 'bytes', 'sha256', 'revisionId'], 'skill file');
  return {
    path: str(row, 'path', 'skill file'),
    mediaType: str(row, 'mediaType', 'skill file'),
    content: str(row, 'content', 'skill file'),
    bytes: int(row, 'bytes', 'skill file'),
    sha256: str(row, 'sha256', 'skill file'),
    revisionId: str(row, 'revisionId', 'skill file'),
  };
}

export function parseSkillRevisionPage(raw: unknown): SkillRevisionPage {
  const row = record(raw, 'revision page');
  only(row, ['items', 'nextCursor'], 'revision page');
  if (!Array.isArray(row.items)) throw new SkillResponseError('revision page.items');
  return {
    items: row.items.map((item) => {
      const entry = record(item, 'revision');
      only(
        entry,
        ['revisionId', 'revision', 'bundleSha256', 'publishedAt', 'publishedBy'],
        'revision',
      );
      return {
        revisionId: str(entry, 'revisionId', 'revision'),
        revision: int(entry, 'revision', 'revision'),
        bundleSha256: str(entry, 'bundleSha256', 'revision'),
        publishedAt: str(entry, 'publishedAt', 'revision'),
        publishedBy: nullableStr(entry, 'publishedBy', 'revision'),
      };
    }),
    nextCursor: nullableStr(row, 'nextCursor', 'revision page'),
  };
}

export function parseSkillWriteReceipt(raw: unknown): SkillWriteReceipt {
  const parsed = SkillWriteReceiptSchema.safeParse(raw);
  if (!parsed.success) throw new SkillResponseError('write receipt');
  return parsed.data;
}

// ---------------------------------------------------------------------------
// Error projection
// ---------------------------------------------------------------------------

/** The one status per skill service error code, mirroring the API's own map. */
export const SKILL_ERROR_STATUS = {
  invalid_input: 400,
  validation: 422,
  limit_exceeded: 422,
  not_found: 404,
  official_source_not_populated: 404,
  forbidden: 403,
  disabled: 403,
  revision_conflict: 409,
  idempotency_conflict: 409,
  slug_conflict: 409,
  lifecycle_conflict: 409,
  official_source_unavailable: 503,
  internal_error: 500,
} as const;

export type SkillErrorCode = keyof typeof SKILL_ERROR_STATUS;

/** The closed detail allowlist the API emits; nothing else survives the projection. */
export const SKILL_ERROR_DETAIL_KEYS = [
  'path',
  'currentVersion',
  'currentDraftVersion',
  'baseRevisionId',
  'rootSha256',
  'bundleSha256',
] as const;

export type SkillErrorDetails = Partial<
  Record<(typeof SKILL_ERROR_DETAIL_KEYS)[number], string | number>
>;

export interface SkillError {
  status: number;
  code: SkillErrorCode;
  message: string;
  reason: string;
  details: SkillErrorDetails;
}

function readErrorDetails(raw: unknown): SkillErrorDetails {
  if (!raw || typeof raw !== 'object') return {};
  const source = raw as Record<string, unknown>;
  const details: SkillErrorDetails = {};
  for (const key of SKILL_ERROR_DETAIL_KEYS) {
    const value = source[key];
    if (typeof value === 'string' || typeof value === 'number') details[key] = value;
  }
  return details;
}

/** The typed skill failure behind an ApiError, or null when it is some other failure. */
export function parseSkillError(err: unknown): SkillError | null {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== 'object') return null;
  const body = (err.body as { error?: unknown }).error;
  if (!body || typeof body !== 'object') return null;
  const row = body as Record<string, unknown>;
  const code = row.code;
  if (typeof code !== 'string' || !(code in SKILL_ERROR_STATUS)) return null;
  return {
    status: err.status,
    code: code as SkillErrorCode,
    message: typeof row.message === 'string' ? row.message : '',
    reason: typeof row.reason === 'string' ? row.reason : '',
    details: readErrorDetails(row.details),
  };
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/** Metadata only: the API decides the real surface from the token, never from this. */
export const SKILL_SURFACE_HEADER = 'X-Parcel-Surface';
const SKILL_SURFACE_VALUE = 'web';

function skillsPath(action: string, workspaceId: string): string {
  return `/workspace/skills/${action}?workspace_id=${encodeURIComponent(workspaceId)}`;
}

function dataOf(envelope: unknown, action: string): unknown {
  return record(envelope, `${action} envelope`).data;
}

function idempotencyKeyOf(input: IdempotentInput): string {
  const key = input.idempotencyKey ?? crypto.randomUUID();
  if (key.length === 0 || key.length > 200) throw new SkillResponseError('idempotency key');
  return key;
}

function boundedSkillLimit(limit: number): number {
  return Math.min(Math.max(Math.trunc(limit), 1), SKILL_PAGE_MAX_LIMIT);
}

/** Drops undefined so a strict route body never sees a key the caller left unset. */
function compact<T extends Record<string, unknown>>(body: T): T {
  return Object.fromEntries(Object.entries(body).filter(([, value]) => value !== undefined)) as T;
}

async function readSkills<T>(
  action: string,
  workspaceId: string,
  body: Record<string, unknown>,
  parse: (raw: unknown) => T,
): Promise<T> {
  const envelope = await apiFetch<unknown>(skillsPath(action, workspaceId), {
    method: 'POST',
    headers: { [SKILL_SURFACE_HEADER]: SKILL_SURFACE_VALUE },
    body: JSON.stringify(compact(body)),
  });
  return parse(dataOf(envelope, action));
}

async function writeSkill(
  action: string,
  workspaceId: string,
  input: IdempotentInput,
  body: Record<string, unknown>,
): Promise<SkillWriteReceipt> {
  const idempotencyKey = idempotencyKeyOf(input);
  const envelope = await apiFetch<unknown>(skillsPath(action, workspaceId), {
    method: 'POST',
    headers: {
      [SKILL_SURFACE_HEADER]: SKILL_SURFACE_VALUE,
      'Idempotency-Key': idempotencyKey,
    },
    body: JSON.stringify(compact(body)),
  });
  return parseSkillWriteReceipt(dataOf(envelope, action));
}

export function findSkills(
  workspaceId: string,
  input: FindSkillInput,
): Promise<SkillCatalogResult> {
  return readSkills(
    'find',
    workspaceId,
    { query: input.query.slice(0, SKILL_QUERY_MAX_LENGTH), limit: input.limit, scope: input.scope },
    parseSkillCatalogResult,
  );
}

export function listSkills(
  workspaceId: string,
  input: ListSkillsInput,
): Promise<SkillCatalogResult> {
  // Bound here too, so a hand-built input cannot turn into a route 400.
  return readSkills(
    'list',
    workspaceId,
    {
      ...input,
      q: input.q == null ? undefined : input.q.slice(0, SKILL_QUERY_MAX_LENGTH),
      limit: input.limit == null ? undefined : boundedSkillLimit(input.limit),
    },
    parseSkillCatalogResult,
  );
}

export function getSkill(workspaceId: string, input: GetSkillInput): Promise<SkillDetail> {
  return readSkills('get', workspaceId, { locator: input.locator }, parseSkillDetail);
}

export function getSkillFile(
  workspaceId: string,
  input: GetSkillFileInput,
): Promise<SkillFileDetail> {
  return readSkills(
    'get-file',
    workspaceId,
    { locator: input.locator, path: input.path },
    parseSkillFileDetail,
  );
}

export function listSkillRevisions(
  workspaceId: string,
  input: ListSkillRevisionsInput,
): Promise<SkillRevisionPage> {
  return readSkills(
    'revisions',
    workspaceId,
    { skillId: input.skillId, limit: input.limit },
    parseSkillRevisionPage,
  );
}

export function createSkill(
  workspaceId: string,
  input: CreateSkillInput,
): Promise<SkillWriteReceipt> {
  return writeSkill('create', workspaceId, input, {
    audience: input.audience,
    bundle: input.bundle,
    override: input.override,
    authoringConversationId: input.authoringConversationId,
  });
}

export function updateSkill(
  workspaceId: string,
  input: UpdateSkillInput,
): Promise<SkillWriteReceipt> {
  return writeSkill('update', workspaceId, input, {
    skillId: input.skillId,
    expectedDraftVersion: input.expectedDraftVersion,
    bundle: input.bundle,
    restoreRevisionId: input.restoreRevisionId,
  });
}

export function publishSkill(
  workspaceId: string,
  input: PublishSkillInput,
): Promise<SkillWriteReceipt> {
  return writeSkill('publish', workspaceId, input, {
    skillId: input.skillId,
    expectedDraftVersion: input.expectedDraftVersion,
  });
}

export function unpublishSkill(
  workspaceId: string,
  input: UnpublishSkillInput,
): Promise<SkillWriteReceipt> {
  return writeSkill('unpublish', workspaceId, input, {
    skillId: input.skillId,
    expectedVersion: input.expectedVersion,
  });
}

export function deleteSkill(
  workspaceId: string,
  input: DeleteSkillInput,
): Promise<SkillWriteReceipt> {
  return writeSkill('delete', workspaceId, input, {
    skillId: input.skillId,
    expectedVersion: input.expectedVersion,
  });
}

export function transferSkill(
  workspaceId: string,
  input: TransferSkillInput,
): Promise<SkillWriteReceipt> {
  return writeSkill('transfer', workspaceId, input, {
    skillId: input.skillId,
    expectedVersion: input.expectedVersion,
    newMaintainerId: input.newMaintainerId,
  });
}

export function moveSkill(workspaceId: string, input: MoveSkillInput): Promise<SkillWriteReceipt> {
  return writeSkill('move', workspaceId, input, {
    skillId: input.skillId,
    expectedVersion: input.expectedVersion,
    audience: input.audience,
  });
}

export function forkSkill(workspaceId: string, input: ForkSkillInput): Promise<SkillWriteReceipt> {
  return writeSkill('fork', workspaceId, input, {
    source: input.source,
    audience: input.audience,
    override: input.override,
    name: input.name,
  });
}

export function installSkill(
  workspaceId: string,
  input: InstallSkillInput,
): Promise<SkillWriteReceipt> {
  return writeSkill('install', workspaceId, input, {
    officialSkillId: input.officialSkillId,
    releaseId: input.releaseId,
    audience: input.audience,
  });
}

export function uninstallSkill(
  workspaceId: string,
  input: UninstallSkillInput,
): Promise<SkillWriteReceipt> {
  return writeSkill('uninstall', workspaceId, input, {
    installationId: input.installationId,
    expectedVersion: input.expectedVersion,
  });
}

export interface SpecSkillsClient {
  find(workspaceId: string, input: FindSkillInput): Promise<SkillCatalogResult>;
  list(workspaceId: string, input: ListSkillsInput): Promise<SkillCatalogResult>;
  get(workspaceId: string, input: GetSkillInput): Promise<SkillDetail>;
  getFile(workspaceId: string, input: GetSkillFileInput): Promise<SkillFileDetail>;
  listRevisions(workspaceId: string, input: ListSkillRevisionsInput): Promise<SkillRevisionPage>;
  create(workspaceId: string, input: CreateSkillInput): Promise<SkillWriteReceipt>;
  update(workspaceId: string, input: UpdateSkillInput): Promise<SkillWriteReceipt>;
  publish(workspaceId: string, input: PublishSkillInput): Promise<SkillWriteReceipt>;
  unpublish(workspaceId: string, input: UnpublishSkillInput): Promise<SkillWriteReceipt>;
  delete(workspaceId: string, input: DeleteSkillInput): Promise<SkillWriteReceipt>;
  transfer(workspaceId: string, input: TransferSkillInput): Promise<SkillWriteReceipt>;
  move(workspaceId: string, input: MoveSkillInput): Promise<SkillWriteReceipt>;
  fork(workspaceId: string, input: ForkSkillInput): Promise<SkillWriteReceipt>;
  install(workspaceId: string, input: InstallSkillInput): Promise<SkillWriteReceipt>;
  uninstall(workspaceId: string, input: UninstallSkillInput): Promise<SkillWriteReceipt>;
}

export const specSkillsClient: SpecSkillsClient = {
  find: findSkills,
  list: listSkills,
  get: getSkill,
  getFile: getSkillFile,
  listRevisions: listSkillRevisions,
  create: createSkill,
  update: updateSkill,
  publish: publishSkill,
  unpublish: unpublishSkill,
  delete: deleteSkill,
  transfer: transferSkill,
  move: moveSkill,
  fork: forkSkill,
  install: installSkill,
  uninstall: uninstallSkill,
};

// ---------------------------------------------------------------------------
// FilterState to list body
// ---------------------------------------------------------------------------

/** The canonical FilterState is the source of truth, so the key and the request agree. */
export function skillListInputFromFilterState(
  filters: FilterState,
  options: { sort?: SkillListSort; direction?: 'asc' | 'desc'; limit?: number } = {},
): ListSkillsInput {
  const search = filterStateToSearch(skillFilters, filters);
  const input: ListSkillsInput = {};
  if (typeof search.q === 'string') input.q = search.q.slice(0, SKILL_QUERY_MAX_LENGTH);
  if (Array.isArray(search.source)) input.source = search.source as SkillCatalogSource[];
  if (Array.isArray(search.lifecycle)) input.lifecycle = search.lifecycle as SkillListLifecycle[];
  if (Array.isArray(search.audience)) input.audience = search.audience as SkillAudience[];
  if (typeof search.installed === 'string') {
    input.installed = search.installed as 'installed' | 'not_installed';
  }
  if (typeof search.updated_after === 'string') input.updated_after = search.updated_after;
  if (typeof search.updated_before === 'string') input.updated_before = search.updated_before;
  if (options.sort) input.sort = options.sort;
  if (options.direction) input.direction = options.direction;
  if (options.limit != null) input.limit = boundedSkillLimit(options.limit);
  return input;
}

export interface SystemSkillDefinitionView {
  key: 'create_skill' | 'improve_skill' | 'add_knowledge' | 'set_up_score';
  version: number;
  name: string;
  description: string;
  instructions: string;
}

/** The workflow a bound conversation runs on, as the API describes it. */
export async function getSystemSkillDefinition(
  workspaceId: string,
  key: string,
  version: number,
): Promise<SystemSkillDefinitionView> {
  return apiFetch<SystemSkillDefinitionView>(
    `/workspaces/${workspaceId}/spec/system-skills/${encodeURIComponent(key)}/${version}`,
  );
}
